import type { SubmitJoinApplicationServiceRequest } from './submit-join-application.dto';

type SnsAccount = SubmitJoinApplicationServiceRequest['snsAccounts'][0];
type SnsType = SnsAccount['snsType'];
type NullableSnsType = '' | null;
type SupportedSnsType = Exclude<SnsType, NullableSnsType>;

interface Platform {
  id: string;
  prefix?: string;
}

const HTTP_PROTOCOL_PATTERN = /^https?:\/\//i;
const URL_PATTERN =
  /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

export const SNS_PLATFORM_TYPE: Record<SupportedSnsType, Platform> = {
  INSTAGRAM: { id: 'instagram', prefix: 'instagram.com/' },
  YOUTUBE: { id: 'youtube', prefix: 'youtube.com/@' },
  TWITTER: { id: 'twitter', prefix: 'x.com/' },
  TIKTOK: { id: 'tiktok', prefix: 'tiktok.com/@' },
  ETC: { id: 'etc' },
} as const;

const sanitizeInput = (value: string): string => value.trim();

const removeLeadingCharacter = (value: string, character: string): string =>
  value.startsWith(character) ? value.slice(1) : value;

const isSupportedSnsType = (snsType: SnsType): snsType is SupportedSnsType =>
  Boolean(snsType && snsType in SNS_PLATFORM_TYPE);

const getPlatformBySnsType = (snsType: SnsType): Platform | undefined =>
  isSupportedSnsType(snsType) ? SNS_PLATFORM_TYPE[snsType] : undefined;

const hasUsableSnsAccount = (
  account: SnsAccount,
): account is SnsAccount & { snsType: Exclude<SnsType, NullableSnsType> } =>
  Boolean(account.snsType && account.snsType !== null && sanitizeInput(account.url));

const normalizePlatformInput = (platform: Platform | undefined, input: string): string => {
  if (!platform?.prefix) return input;

  const withoutPrefix = input.startsWith(platform.prefix)
    ? input.slice(platform.prefix.length)
    : input;

  return platform.prefix.includes('@') ? removeLeadingCharacter(withoutPrefix, '@') : withoutPrefix;
};

export const isValidUrl = (input: string): boolean => {
  const trimmed = input.trim();

  if (!trimmed) {
    return false;
  }

  if (!URL_PATTERN.test(trimmed)) {
    return false;
  }

  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const validateEtcUrl = (
  userInput: string,
): {
  isValid: boolean;
  message?: string;
} => {
  const trimmed = sanitizeInput(userInput);

  if (!trimmed) {
    return { isValid: false, message: 'URL을 입력해주세요.' };
  }

  if (!isValidUrl(trimmed)) {
    return {
      isValid: false,
      message: '올바른 URL 형식을 입력해주세요.',
    };
  }

  return { isValid: true };
};

export const convertToFullUrl = (snsType: SnsType, userInput: string): string => {
  const trimmedInput = sanitizeInput(userInput);
  if (!trimmedInput) return '';

  if (HTTP_PROTOCOL_PATTERN.test(trimmedInput)) {
    return trimmedInput;
  }

  const platform = getPlatformBySnsType(snsType);

  if (snsType === 'ETC' && !platform?.prefix) {
    const withHttps = `https://${trimmedInput}`;
    if (isValidUrl(withHttps)) {
      return withHttps;
    }
    return trimmedInput;
  }

  if (!platform?.prefix) {
    return trimmedInput;
  }

  const sanitizedInput = normalizePlatformInput(platform, trimmedInput);

  const prefixWithWww = platform.prefix.startsWith('www.')
    ? platform.prefix
    : `www.${platform.prefix}`;

  return `https://${prefixWithWww}${sanitizedInput}`;
};

export const toNormalizedSnsAccounts = (
  accounts: SnsAccount[],
): (SnsAccount & { snsType: Exclude<SnsType, NullableSnsType>; url: string })[] =>
  accounts.filter(hasUsableSnsAccount).map((account) => ({
    ...account,
    url: convertToFullUrl(account.snsType, account.url),
  }));

export const toNormalizedSnsUrls = (accounts: SnsAccount[]): string[] =>
  toNormalizedSnsAccounts(accounts)
    .map((account) => account.url)
    .filter((url) => Boolean(url));

export const extractUserInput = (platform: Platform, fullUrl: string): string => {
  const trimmedUrl = sanitizeInput(fullUrl);
  if (!trimmedUrl) return '';

  const expectedPrefix = platform.prefix ? `https://${platform.prefix}` : '';

  if (expectedPrefix && trimmedUrl.startsWith(expectedPrefix)) {
    const remainder = trimmedUrl.slice(expectedPrefix.length);
    return normalizePlatformInput(platform, remainder);
  }

  if (platform.prefix && trimmedUrl.startsWith(platform.prefix)) {
    const remainder = trimmedUrl.slice(platform.prefix.length);
    return normalizePlatformInput(platform, remainder);
  }

  return trimmedUrl.replace(HTTP_PROTOCOL_PATTERN, '');
};

export const platformIdToSnsType = (platformId: string): SnsType =>
  platformId.toUpperCase() as SnsType;

export const snsTypeToPlatformId = (snsType: SnsType): string =>
  snsType ? snsType.toLocaleUpperCase() : '';

export const updateSnsAccount = (
  accounts: SnsAccount[],
  index: number,
  updates: Partial<SnsAccount>,
): SnsAccount[] => {
  const updatedAccounts = [...accounts];

  if (updatedAccounts[index]) {
    updatedAccounts[index] = { ...updatedAccounts[index], ...updates };
  } else {
    updatedAccounts[index] = {
      snsType: null,
      url: '',
      ...updates,
    };
  }

  return updatedAccounts;
};
